import type {
  DomainPropertySummary,
  DomainPropertyIR,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

export function buildRequiredFieldMutations(
  fieldPath: string,
  required?: boolean,
) {
  if (!required) return [];
  return [
    {
      label: `set required on ${fieldPath}`,
      query: `mutation { 
        at(path: ${JSON.stringify(fieldPath)}) { 
          set_required {
            model { path }
          }
        }
      }`,
    },
  ];
}

export const requiredMetamodelPackage = defineMetamodelPackage({
  id: "required",
  docs: {
    fieldRows: [
      { key: "required", description: "Marks the field as required." },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type RequiredMetamodel {
        model: Model!
        message: String
      }

      extend type Model {
        required_rule: RequiredMetamodel
      }

      extend type ModelMutation {
        set_required(message: String): RequiredMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        RequiredMetamodel: {
          model: (value) => value.model,
          message: (value) => value.message || null,
        },
        Model: {
          required_rule: async (ant) => await run(ant.required_rule()),
        },
        ModelMutation: {
          set_required: async (ant, { message }) => {
            const model = await run(ant.set_required(message));
            return { model, message };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildRequiredFieldMutations(fieldPath, spec.required);
    },
  },
  summary: {
    selections: {
      propertyFields: [`required_rule { message }`],
    },
    readPropertySummary(rawProperty) {
      if (!rawProperty.required_rule) {
        return { required: null };
      }

      return typeof rawProperty.required_rule.message === "string"
        ? { required: { message: rawProperty.required_rule.message } }
        : { required: {} };
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      propertySummary: DomainPropertySummary,
    ) {
      if (!propertySummary.required) return propertyIR;

      return {
        ...propertyIR,
        docs: [
          ...propertyIR.docs,
          propertySummary.required.message || "Required.",
        ],
      };
    },
  },
});
